import React, { useState, useEffect, useContext, useCallback, useMemo } from "react"
import useStyles from "./styles"
import { useHistory } from "react-router-dom"
import { CardHeader, IconButton } from "@material-ui/core"
import AvatarComBadge from "../AvatarComBadge"
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft"
import { Tooltip } from "@material-ui/core"
import useContactsStore from "../../stores/ContactsStore"
import textoTruncar from "../../utils/textoTruncar"
import { AuthContext } from "../../context/Auth/AuthContext"
import obterNomeContatoConversa from "../../helpers/obterNomeContatoConversa"
import { canais } from "../../utils/canais"

const TicketInfo = ({ ticket, onClick, ehEspiar, contact }) => {
  const { user: oUsuarioLogado } = useContext(AuthContext)

  // Store > prop contact > ticket.contact (espelha CustomCard / lista Conversas)
  const contactId = ticket?.contact?.id ?? contact?.id
  const selectorContato = useCallback(
    (state) => state.contacts.find((c) => c.id === contactId) ?? null,
    [contactId],
  )
  const oContatoFromStore = useContactsStore(selectorContato)
  const oContatoMesclado = oContatoFromStore || contact || ticket?.contact
  const oTicketParaExibicao = useMemo(() => {
    if (!ticket) return ticket
    if (oContatoMesclado) {
      return { ...ticket, contact: oContatoMesclado }
    }
    return ticket
  }, [ticket, oContatoMesclado])

  const [cUserName, setUserName] = useState("")
  const [cContactName, setContactName] = useState("")
  const [cImagemPerfil, setImagemPerfil] = useState(null)
  const [cImagemPerfilSecundaria, setImagemPerfilSecundaria] = useState(null)
  const [cNomeAvatarPrincipal, setNomeAvatarPrincipal] = useState("")
  const [cNomeAvatarSecundario, setNomeAvatarSecundario] = useState("")
  const [cAbreviacaoPrincipal, setAbreviacaoPrincipal] = useState("")
  const [cAbreviacaoSecundaria, setAbreviacaoSecundaria] = useState("")
  const [lMostrarDoisAvatares, setMostrarDoisAvatares] = useState(false)
  const [oContatoExibir, setContatoExibir] = useState(null)
  const history = useHistory()

  const classes = useStyles()

  useEffect(() => {
    if (!oTicketParaExibicao || !oUsuarioLogado) return

    const lEhConversaInterna = oTicketParaExibicao.canalTipoId === canais.interno

    if (lEhConversaInterna) {
      const oResponsavel = oTicketParaExibicao.responsavel
      const oUsuario = oTicketParaExibicao.usuario

      // Verifica se o usuário logado participa da conversa
      const lUsuarioLogadoEhResponsavel = oResponsavel && oResponsavel.id === oUsuarioLogado.id
      const lUsuarioLogadoEhUsuario = oUsuario && oUsuario.id === oUsuarioLogado.id
      const lUsuarioLogadoParticipa = lUsuarioLogadoEhResponsavel || lUsuarioLogadoEhUsuario

      if (lUsuarioLogadoParticipa) {
        setMostrarDoisAvatares(false)
        setImagemPerfilSecundaria(null)
        setNomeAvatarSecundario("")
        setAbreviacaoSecundaria("")

        if (lUsuarioLogadoEhResponsavel) {
          setContactName(oUsuario?.name || "Usuário sem nome")
          setImagemPerfil(oUsuario?.imagemPerfil || null)
          setNomeAvatarPrincipal(oUsuario?.name || "")
          setAbreviacaoPrincipal(oUsuario?.nomeAbreviacao || "")
          setContatoExibir(oUsuario)
        } else {
          setContactName(oResponsavel?.name || "Responsável sem nome")
          setImagemPerfil(oResponsavel?.imagemPerfil || null)
          setNomeAvatarPrincipal(oResponsavel?.name || "")
          setAbreviacaoPrincipal(oResponsavel?.nomeAbreviacao || "")
          setContatoExibir(oResponsavel)
        }
      } else {
        setMostrarDoisAvatares(true)
        setImagemPerfil(oResponsavel?.imagemPerfil || null)
        setImagemPerfilSecundaria(oUsuario?.imagemPerfil || null)
        setNomeAvatarPrincipal(oResponsavel?.name || "")
        setNomeAvatarSecundario(oUsuario?.name || "")
        setAbreviacaoPrincipal(oResponsavel?.nomeAbreviacao || "")
        setAbreviacaoSecundaria(oUsuario?.nomeAbreviacao || "")

        const cNomeResponsavel = oResponsavel?.name || "Responsável"
        const cNomeUsuario = oUsuario?.name || "Usuário"
        setContactName(`${cNomeResponsavel} e ${cNomeUsuario}`)
        setContatoExibir({ responsavel: oResponsavel, usuario: oUsuario, mostrarDois: true })
      }

      setUserName("Chat interno")
    } else {
      // Para conversas externas, usa o helper com contato mesclado (store > prop > ticket)
      const { cNome, cFoto } = obterNomeContatoConversa(oTicketParaExibicao, oUsuarioLogado.id)

      setContactName(cNome)
      setImagemPerfil(cFoto)
      setNomeAvatarPrincipal(cNome)
      setAbreviacaoPrincipal("")
      setNomeAvatarSecundario("")
      setAbreviacaoSecundaria("")
      setMostrarDoisAvatares(false)
      setImagemPerfilSecundaria(null)
      setContatoExibir(oContatoMesclado)

      // Define o nome do responsável
      if (oTicketParaExibicao.responsavel) {
        const cNomeResponsavel = `Responsável: ${oTicketParaExibicao.responsavel.name}`
        setUserName(cNomeResponsavel)

        if (document.body.offsetWidth < 600) {
          setUserName(oTicketParaExibicao.responsavel.name)
        }
      } else {
        setUserName("")
      }
    }
  }, [oTicketParaExibicao, oContatoMesclado, oUsuarioLogado])

  const handleBackClick = () => {
    history.push("/conversas")
  }

  const handleClick = (e) => {
    if (onClick) {
      onClick(e, oContatoExibir)
    }
  }

  return (
    <CardHeader
      className={classes.ticketInfo}
      onClick={handleClick}
      style={{ cursor: "pointer" }}
      titleTypographyProps={{ noWrap: true }}
      subheaderTypographyProps={{ noWrap: true }}
      avatar={
        <div style={{ display: "flex", alignItems: "center" }}>
          {!ehEspiar && (
            <Tooltip title="Retornar">
              <IconButton aria-label="Retornar para conversas" onClick={handleBackClick} style={{ marginRight: 8, padding: 8 }}>
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          )}
          <AvatarComBadge
            cSrcPrincipal={cImagemPerfil}
            cSrcSecundario={cImagemPerfilSecundaria}
            lMostrarDoisAvatares={lMostrarDoisAvatares}
            cNomePrincipal={cNomeAvatarPrincipal}
            cNomeAbreviacao={cAbreviacaoPrincipal}
            cNomeSecundario={cNomeAvatarSecundario}
            cNomeAbreviacaoSecundario={cAbreviacaoSecundaria}
            cTipoCanal={ticket?.canalTipoId === canais.interno ? "interno" : undefined}
            nTamanho={42}
            nTamanhoAvatarDuplo={32}
            nTamanhoContainer={48}
          />
        </div>
      }
      title={`${textoTruncar(cContactName, document.body.offsetWidth < 600 ? 60 : 35)}`}
      subheader={cUserName ? cUserName : undefined}
    />
  )
}

export default TicketInfo
